/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React from 'react';
import UniversalTracker from '../../../../model/UniversalTracker';
import { getGroup, Group, standards } from '@g17eco/core';
import { ToolbarButtonProps } from './ToolbarButton';
import { SurveyModelMinData } from '../../../../model/surveyData';
import config from '../../../../config';
import { QUESTION } from '../../../../constants/terminology';

interface StandardButtonProps {
  utr: UniversalTracker;
  selectedStandard: string;
  handleStandardsClick: (value: string) => void;
}

export interface StandardsButtons extends StandardButtonProps {
  standardShow: number;
  showSelectedStandard?: boolean;
  survey: Pick<SurveyModelMinData, 'scope'>;
}

interface StandardButton extends StandardButtonProps {
  standardCode: string;
}

const getTooltip = (utr: UniversalTracker, code: any) => {
  return (
    <div className='m-1 text-left'>
      <div className='strong'>{getGroup('standards', code)?.shortName} {utr.getTypeCode(code)}</div>
      <div>{utr.getValueLabel(code)}</div>
      <div>Click to see full {QUESTION.CAPITALIZED_SINGULAR} Text</div>
    </div>
  )
}

export const getStandardButton = ({ utr, selectedStandard, standardCode, handleStandardsClick }: StandardButton) => {
  const standard = getGroup('standards', standardCode);
  if (!standard) {
    return { contents: <></> }
  }
  return {
    contents: <div className='img-container'> <img className='icon-img' src={standard.src} alt={standard.name} /></div>,
    tooltip: getTooltip(utr, standardCode),
    btnClass: selectedStandard === standardCode ? 'active' : '',
    onClick: () => handleStandardsClick(standardCode)
  }
}

const append = (acc: Group[], toAdd: Group) => [...acc, toAdd];
const replace = (acc: Group[], toRemove: Group, toAdd: Group) => [...acc.filter(g => g.code !== toRemove.code), toAdd];

const standardsReducer = (acc: Group[], newStandard: Group, scopeCodes: string[]): Group[] => {

  if (!newStandard.versionGroupCode || !newStandard.version) {
    return append(acc, newStandard);
  }

  const existingStandard = acc.find(s => s.versionGroupCode === newStandard.versionGroupCode);
  if (existingStandard) {

    const isNewInScope = scopeCodes.includes(newStandard.code);
    const isExistingInScope = scopeCodes.includes(existingStandard.code);
    if (isNewInScope && isExistingInScope) {
      // If both are in scope, show both icons
      return append(acc, newStandard);
    }

    // If only one in scope, prioritize that one
    if (!isNewInScope && isExistingInScope) {
      return acc;
    }
    if (isNewInScope && !isExistingInScope) {
      return replace(acc, existingStandard, newStandard);
    }

    // Neither are in scope, so choose latest version only
    const existingVersion = existingStandard?.version ?? 0;
    if (newStandard.version > existingVersion) {
      return replace(acc, existingStandard, newStandard);
    }
    return acc;
  }
  return append(acc, newStandard);
}

export const getStandardButtons = (props: StandardsButtons) => {
  const { utr, survey } = props;

  const scopeCodes = survey?.scope?.standards ?? [];

  const items: ToolbarButtonProps[] = Object.values(standards)
    .filter(standard => utr.hasAlternativeInfo(standard.code))
    .reduce((acc, standard) => standardsReducer(acc, standard, scopeCodes), [] as Group[])
    .map(standard => getStandardButton({ ...props, standardCode: standard.code }));

  if (items.length === 0) {
    const utrType = utr.getType();
    if (utrType === 'custom_kpi') {
      items.push({
        contents: <i className='fa fa-crosshairs fa-2x' title='Custom KPI' />,
        tooltip: utr.getValueLabel(undefined),
        btnClass: 'active',
      });
    } else {
      items.push({
        contents: <img src={`${config.media.logosBaseUrl}/wwg.png`} alt='WWG' />,
        tooltip: utr.getValueLabel(undefined),
        btnClass: 'active',
        onClick: () => props.handleStandardsClick('wwg')
      });
    }
  }
  return items;
}
